// src/components/RoleSwitcher.jsx
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, User, Shield, GraduationCap, Home } from 'lucide-react';
import { useCurrentUser, useHasMultipleContexts, useSwitchRole, getRoleHomePath, getTeacherBranchIds } from '@/hooks/useAuth';
import { useAuth } from '@/lib/AuthContext';
import { useTheme } from '@/lib/ThemeContext';
import { useLanguage } from '@/components/LanguageContext';
import { useBranchesByIds } from '@/hooks/useBranches';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const ROLE_CONFIG = {
  super_admin: {
    labelKey: 'roleSuperAdmin',
    icon: Shield,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    descriptionKey: 'roleDescriptionPlatformManagement'
  },
  branch_admin: {
    labelKey: 'roleBranchAdmin',
    icon: Home,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    descriptionKey: 'roleDescriptionBranchManagement'
  },
  teacher: {
    labelKey: 'roleTeacher',
    icon: GraduationCap,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    descriptionKey: 'roleDescriptionGroupManagement'
  },
  parent: {
    labelKey: 'roleParent',
    icon: User,
    color: 'text-pink-600',
    bgColor: 'bg-pink-50',
    descriptionKey: 'roleDescriptionChildView'
  }
};

/** Build list of contexts: roles expanded per branch for branch_admin and teacher */
function buildContexts(roles, adminBranchIds, adminBranches, teacherBranchIds, teacherBranches, primary_role) {
  const list = [];
  const adminIds = Array.isArray(adminBranchIds) ? adminBranchIds : [];
  const teacherIds = Array.isArray(teacherBranchIds) ? teacherBranchIds : [];
  for (const role of roles || []) {
    if (role === 'branch_admin' && adminIds.length > 0) {
      for (const id of adminIds) {
        const b = adminBranches?.find((x) => x.id === id);
        list.push({
          role: 'branch_admin',
          branchId: id,
          branchName: b ? (b.display_name || b.name) : null
        });
      }
    } else if (role === 'teacher' && teacherIds.length > 0) {
      for (const id of teacherIds) {
        const b = teacherBranches?.find((x) => x.id === id);
        list.push({
          role: 'teacher',
          branchId: id,
          branchName: b ? (b.display_name || b.name) : null
        });
      }
    } else {
      list.push({ role, branchId: null, branchName: null });
    }
  }
  return list;
}

export const RoleSwitcher = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { getActiveColors } = useTheme();
  const { refreshProfile } = useAuth();
  const { data: user } = useCurrentUser();
  const hasMultipleContexts = useHasMultipleContexts();
  const switchRole = useSwitchRole();
  const [isOpen, setIsOpen] = useState(false);
  const roleColors = getActiveColors();

  const { roles = [], active_role, primary_role, current_branch_id, branch_ids } = user?.profile || {};
  const teacherBranchIds = useMemo(() => getTeacherBranchIds(user?.profile), [user?.profile]);

  const { data: adminBranches = [] } = useBranchesByIds(
    roles?.includes('branch_admin') ? branch_ids : [],
    { enabled: roles?.includes('branch_admin') && (branch_ids?.length || 0) > 0 }
  );
  const { data: teacherBranches = [] } = useBranchesByIds(
    roles?.includes('teacher') ? teacherBranchIds : [],
    { enabled: roles?.includes('teacher') && teacherBranchIds.length > 0 }
  );

  const contexts = useMemo(
    () => buildContexts(
      roles,
      roles?.includes('branch_admin') ? branch_ids : null,
      adminBranches,
      roles?.includes('teacher') ? teacherBranchIds : null,
      teacherBranches,
      primary_role
    ),
    [roles, branch_ids, adminBranches, teacherBranchIds, teacherBranches, primary_role]
  );

  if (!hasMultipleContexts || !user?.profile || contexts.length === 0) {
    return null;
  }

  const activeRoleConfig = ROLE_CONFIG[active_role] || ROLE_CONFIG.parent;
  const ActiveIcon = activeRoleConfig.icon;
  const currentBranchName = current_branch_id && (active_role === 'branch_admin' || active_role === 'teacher')
    ? (adminBranches.find(b => b.id === current_branch_id) || teacherBranches.find(b => b.id === current_branch_id))?.display_name
    || (adminBranches.find(b => b.id === current_branch_id) || teacherBranches.find(b => b.id === current_branch_id))?.name
    : null;
  const triggerLabel = currentBranchName
    ? `${t(activeRoleConfig.labelKey)} – ${currentBranchName}`
    : t(activeRoleConfig.labelKey);

  const handleContextSwitch = (ctx) => {
    const isActive = ctx.role === active_role && (ctx.branchId == null || ctx.branchId === current_branch_id);
    if (isActive) {
      setIsOpen(false);
      return;
    }
    const payload = ctx.branchId ? { role: ctx.role, branchId: ctx.branchId } : ctx.role;
    switchRole.mutate(payload, {
      onSuccess: async (data) => {
        await refreshProfile();
        navigate(getRoleHomePath(data.active_role));
      }
    });
    setIsOpen(false);
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <div
          className={cn(
            'p-4 rounded-xl border backdrop-blur-sm hover:bg-white/20 transition-all cursor-pointer',
            roleColors.infoCardBg
          )}
        >
          <div className="flex items-center gap-3">
            <div className={cn('flex h-10 w-10 items-center justify-center rounded-full ring-2 ring-white/50 shadow-md', roleColors.infoCardBg)}>
              <ActiveIcon className={cn('h-5 w-5', roleColors.infoCardIcon)} />
            </div>
            <div className="flex-1 min-w-0">
              <p className={cn('text-sm font-semibold truncate', roleColors.infoCardText)}>
                {triggerLabel}
              </p>
              <p className={cn('text-xs', roleColors.infoCardLabel)}>
                {t('switchRole')}
              </p>
            </div>
            <ChevronRight className={cn('h-4 w-4 shrink-0', roleColors.infoCardLabel)} />
          </div>
        </div>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-64 rounded-xl border border-slate-200/80 bg-white/95 p-2 shadow-xl backdrop-blur-sm dark:border-slate-700 dark:bg-slate-900/95"
      >
        <DropdownMenuLabel className="px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {t('switchRole')}
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="mx-1 my-1 bg-slate-100 dark:bg-slate-800" />

        {contexts.map((ctx) => {
          const config = ROLE_CONFIG[ctx.role];
          if (!config) return null;
          const Icon = config.icon;
          const isActive = ctx.role === active_role && (ctx.branchId == null || ctx.branchId === current_branch_id);
          const isPrimary = ctx.role === primary_role && !ctx.branchId;
          const label = ctx.branchId
            ? `${t(config.labelKey)} – ${ctx.branchName || '...'}`
            : t(config.labelKey);

          return (
            <DropdownMenuItem
              key={ctx.branchId ? `${ctx.role}-${ctx.branchId}` : ctx.role}
              onClick={() => handleContextSwitch(ctx)}
              className={cn(
                'flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 outline-none transition-colors focus:bg-slate-50 dark:focus:bg-slate-800/80',
                isActive
                  ? cn(config.bgColor, config.color, 'border-l-2 border-current')
                  : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
              )}
            >
              <div
                className={cn(
                  'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
                  isActive ? config.bgColor : 'bg-slate-100 dark:bg-slate-800'
                )}
              >
                <Icon className={cn('h-4 w-4', isActive ? config.color : 'text-slate-500 dark:text-slate-400')} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span className={cn('text-sm', isActive && 'font-semibold')}>
                    {label}
                  </span>
                  {isPrimary && (
                    <span className="text-xs text-slate-400 dark:text-slate-500">
                      ({t('defaultRole')})
                    </span>
                  )}
                </div>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  {t(config.descriptionKey)}
                </p>
              </div>
              {isActive && (
                <div className="h-2 w-2 shrink-0 rounded-full bg-green-600" />
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/** Circular button with role icon for mobile header – sits next to profile avatar. */
export const RoleSwitcherMobileButton = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { refreshProfile } = useAuth();
  const { data: user } = useCurrentUser();
  const hasMultipleContexts = useHasMultipleContexts();
  const switchRole = useSwitchRole();

  const { roles = [], active_role, primary_role, current_branch_id, branch_ids } = user?.profile || {};
  const teacherBranchIds = useMemo(() => getTeacherBranchIds(user?.profile), [user?.profile]);

  const { data: adminBranches = [] } = useBranchesByIds(
    roles?.includes('branch_admin') ? branch_ids : [],
    { enabled: roles?.includes('branch_admin') && (branch_ids?.length || 0) > 0 }
  );
  const { data: teacherBranches = [] } = useBranchesByIds(
    roles?.includes('teacher') ? teacherBranchIds : [],
    { enabled: roles?.includes('teacher') && teacherBranchIds.length > 0 }
  );

  const contexts = useMemo(
    () => buildContexts(
      roles,
      roles?.includes('branch_admin') ? branch_ids : null,
      adminBranches,
      roles?.includes('teacher') ? teacherBranchIds : null,
      teacherBranches,
      primary_role
    ),
    [roles, branch_ids, adminBranches, teacherBranchIds, teacherBranches, primary_role]
  );

  if (!hasMultipleContexts || !user?.profile || contexts.length === 0) {
    return null;
  }

  const activeRoleConfig = ROLE_CONFIG[active_role] || ROLE_CONFIG.parent;
  const ActiveIcon = activeRoleConfig.icon;

  const handleContextSwitch = (ctx) => {
    const isActive = ctx.role === active_role && (ctx.branchId == null || ctx.branchId === current_branch_id);
    if (isActive) return;
    const payload = ctx.branchId ? { role: ctx.role, branchId: ctx.branchId } : ctx.role;
    switchRole.mutate(payload, {
      onSuccess: async (data) => {
        await refreshProfile();
        navigate(getRoleHomePath(data.active_role));
      }
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 rounded-full ring-2 ring-white/50 shadow-md bg-white/10 text-white hover:bg-white/20 hover:text-white"
          aria-label={t('switchRole')}
        >
          <ActiveIcon className="h-5 w-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        side="bottom"
        sideOffset={8}
        className="w-64 rounded-xl border border-slate-200/80 bg-white/95 p-2 shadow-xl backdrop-blur-sm dark:border-slate-700 dark:bg-slate-900/95"
      >
        <DropdownMenuLabel className="px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {t('switchRole')}
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="mx-1 my-1 bg-slate-100 dark:bg-slate-800" />
        {contexts.map((ctx) => {
          const config = ROLE_CONFIG[ctx.role];
          if (!config) return null;
          const Icon = config.icon;
          const isActive = ctx.role === active_role && (ctx.branchId == null || ctx.branchId === current_branch_id);
          const isPrimary = ctx.role === primary_role && !ctx.branchId;
          const label = ctx.branchId
            ? `${t(config.labelKey)} – ${ctx.branchName || '...'}`
            : t(config.labelKey);
          return (
            <DropdownMenuItem
              key={ctx.branchId ? `${ctx.role}-${ctx.branchId}` : ctx.role}
              onClick={() => handleContextSwitch(ctx)}
              className={cn(
                'flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 outline-none transition-colors focus:bg-slate-50 dark:focus:bg-slate-800/80',
                isActive
                  ? cn(config.bgColor, config.color, 'border-l-2 border-current')
                  : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
              )}
            >
              <div
                className={cn(
                  'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
                  isActive ? config.bgColor : 'bg-slate-100 dark:bg-slate-800'
                )}
              >
                <Icon className={cn('h-4 w-4', isActive ? config.color : 'text-slate-500 dark:text-slate-400')} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span className={cn('text-sm', isActive && 'font-semibold')}>
                    {label}
                  </span>
                  {isPrimary && (
                    <span className="text-xs text-slate-400 dark:text-slate-500">
                      ({t('defaultRole')})
                    </span>
                  )}
                </div>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  {t(config.descriptionKey)}
                </p>
              </div>
              {isActive && (
                <div className="h-2 w-2 shrink-0 rounded-full bg-green-600" />
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Compact version for mobile sheet – select dropdown (or use RoleSwitcherMobileButton in header)
export const RoleSwitcherMobile = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { getActiveColors } = useTheme();
  const { refreshProfile } = useAuth();
  const { data: user } = useCurrentUser();
  const hasMultipleContexts = useHasMultipleContexts();
  const switchRole = useSwitchRole();
  const roleColors = getActiveColors();

  const { roles = [], active_role, current_branch_id, branch_ids } = user?.profile || {};
  const teacherBranchIds = useMemo(() => getTeacherBranchIds(user?.profile), [user?.profile]);

  const { data: adminBranches = [] } = useBranchesByIds(
    roles?.includes('branch_admin') ? branch_ids : [],
    { enabled: roles?.includes('branch_admin') && (branch_ids?.length || 0) > 0 }
  );
  const { data: teacherBranches = [] } = useBranchesByIds(
    roles?.includes('teacher') ? teacherBranchIds : [],
    { enabled: roles?.includes('teacher') && teacherBranchIds.length > 0 }
  );

  const contexts = useMemo(
    () => buildContexts(
      roles,
      roles?.includes('branch_admin') ? branch_ids : null,
      adminBranches,
      roles?.includes('teacher') ? teacherBranchIds : null,
      teacherBranches
    ),
    [roles, branch_ids, adminBranches, teacherBranchIds, teacherBranches]
  );

  if (!hasMultipleContexts || !user?.profile || contexts.length === 0) {
    return null;
  }

  const currentValue = (active_role === 'branch_admin' || active_role === 'teacher') && current_branch_id
    ? `${active_role}:${current_branch_id}`
    : active_role;

  const handleChange = (value) => {
    const branchAdminMatch = value.startsWith('branch_admin:');
    const teacherMatch = value.startsWith('teacher:');
    const payload = branchAdminMatch
      ? { role: 'branch_admin', branchId: value.slice('branch_admin:'.length) }
      : teacherMatch
        ? { role: 'teacher', branchId: value.slice('teacher:'.length) }
        : value;
    switchRole.mutate(payload, {
      onSuccess: async (data) => {
        await refreshProfile();
        navigate(getRoleHomePath(data.active_role));
      }
    });
  };

  return (
    <div className={cn('mx-4 mt-4 p-4 rounded-xl border backdrop-blur-sm', roleColors.infoCardBg)}>
      <p className={cn('text-xs font-medium mb-2', roleColors.infoCardLabel)}>
        {t('activeRole')}
      </p>
      <select
        value={currentValue}
        onChange={(e) => handleChange(e.target.value)}
        className={cn(
          'w-full p-3 rounded-lg border bg-white/10 border-white/20 text-white font-medium',
          'focus:outline-none focus:ring-2 focus:ring-white/30'
        )}
      >
        {contexts.map((ctx) => {
          const config = ROLE_CONFIG[ctx.role];
          if (!config) return null;
          const optionValue = ctx.branchId ? `${ctx.role}:${ctx.branchId}` : ctx.role;
          const label = ctx.branchId
            ? `${t(config.labelKey)} – ${ctx.branchName || '...'}`
            : t(config.labelKey);
          return (
            <option key={ctx.branchId ? `${ctx.role}-${ctx.branchId}` : ctx.role} value={optionValue} className="bg-slate-800 text-white">
              {label}
            </option>
          );
        })}
      </select>
    </div>
  );
};
